import * as yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';

import {
  GetAppQuery as GetApp, Permission,
} from '__generated__/graphql';
import { gql } from '__generated__';

import { assetUrl, getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import { useSession } from '../Auth/SessionProvider';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useValidation from '../../common/useValidation';

const addAppSchema = yup.object().shape({
  project: yup.string().required(),
});

export const GetAppQuery = gql(`
  query GetApp($id: ID!) {
    app(id: $id) {
      id
      name
      description
      logo_url
      requested_permissions
      project {
        id
        organisation_name
        verified
      }
    }
  }
`);

export const AuthorizeAppToProjectMutation = gql(`
  mutation AuthorizeAppToProject($project_id: ID!, $app_id: ID!) {
    authorizeAppToProject(project_id: $project_id, app_id: $app_id) {
      id
      project {
        id
      }
    }
  }
`);

type App = GetApp['app'];

export interface ConnectAppPageProps {
  appId: string;
}

const ConnectAppPage = ({ appId }: ConnectAppPageProps) => {
  const { data } = useQuery(
    GetAppQuery,
    {
      variables: {
        id: appId,
      },
    },
  );

  const app = data?.app;

  if (!app) {
    return <UI.PageLoader />;
  }

  return <ConnectAppForm app={app} />;
};

interface ConnectAppFormProps {
  app: App;
}

const ConnectAppForm = ({ app }: ConnectAppFormProps) => {
  const { t } = useTranslation();
  const { user } = useSession();
  const notifier = useNotifier();
  const history = useHistory();

  const form = useForm({
    values: {
      permissions: app.requested_permissions,
      project: '',
    },
  });

  const [addApp, { error, loading }] = useMutation(
    AuthorizeAppToProjectMutation,
    {
      variables: {
        app_id: app.id,
        project_id: form.values.project,
      },
      onCompleted: ({ authorizeAppToProject }) => {
        notifier.success(t('notifications.app_connected'));

        history.push(`/${authorizeAppToProject?.project.id}/project/integrations`);
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addAppSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Section sc={{ compact: true }}>
      <UI.GridContainer>
        <UI.Div sc={{ textAlign: 'center' }}>
          <img src={app.logo_url || assetUrl('images/rocket.svg')} alt="" height={120} />
          <UI.H1 sc={{ my: 1 }}>
            {t('connect_with', { title: app.name })}
          </UI.H1>
          <UI.Paragraph sc={{ big: true, mb: 0 }} style={{ textWrap: 'balance' }}>
            {t('connect_app_intro')}
          </UI.Paragraph>
        </UI.Div>

        <UI.Card sc={{ spacing: 'lg' }}>
          {!app.project.verified && (
            <UI.Warning>
              {t('app_not_verified_warning')}
            </UI.Warning>
          )}

          {app.project.verified && (
            <UI.Form onSubmit={() => addApp()}>
              <UI.FormGrid>
                {app.description && (
                  <>
                    <UI.Div>
                      <UI.H3 sc={{ mb: 1 }}>{app.name}</UI.H3>
                      <UI.NL2BR content={app.description} />
                    </UI.Div>

                    <UI.HR />
                  </>
                )}

                <UI.ServerErrors error={error} />

                <UI.InputGroup sc={{ valid: !errors.project, touched: form.touched.project }}>
                  <UI.InputLabel htmlFor="Project">
                    {t('project')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <UI.Select
                    {...form.select('project')}
                    id="Project"
                    sc={{ size: 'lg', hasValue: !!form.values.project }}
                  >
                    <option value="">{t('select_project')}</option>
                    {user.project_memberships.map(({ project, permissions }) => (
                      <option
                        {...form.option(project.id)}
                        disabled={!permissions.includes(Permission.admin)}
                        key={project.id}
                      >
                        {project.organisation_name}
                        {!permissions.includes(Permission.admin) && (
                          <>
                            {' '}
                            (
                            {t('not_authorised')}
                            )
                          </>
                        )}
                      </option>
                    ))}
                  </UI.Select>
                  <UI.ErrorMessages attribute={t('project')} errors={errors.project} />
                </UI.InputGroup>

                <UI.InputGroup>
                  <UI.InputLabel>
                    {t('requested_permissions')}
                  </UI.InputLabel>
                  <UI.UL>
                    {app.requested_permissions.map((permission) => (
                      <UI.LI key={permission}>
                        <UI.Span sc={{ fontWeight: 500, block: true }}>
                          {t(`permissions.${permission}`)}
                        </UI.Span>
                        <UI.Span sc={{ color: 'gray.500' }}>
                          {t(`permissions.${permission}_description`)}
                        </UI.Span>
                      </UI.LI>
                    ))}
                  </UI.UL>
                </UI.InputGroup>

                <UI.Info>
                  <Trans
                    i18nKey="connect_app_description"
                    values={{
                      name: app.project.organisation_name,
                      app: app.name,
                    }}
                  >
                    <UI.Strong />
                  </Trans>
                </UI.Info>

                <UI.Button
                  type="submit"
                  sc={{ brand: 'secondary', size: 'lg', loading, width: ['100%', 'auto'] }}
                  disabled={!form.changed() || loading || !valid}
                >
                  {t('connect_app')}
                  {' '}
                  {loading && <UI.Loader sc={{ size: 12, brand: 'secondary.200', ml: 1 }} />}
                </UI.Button>
              </UI.FormGrid>
            </UI.Form>
          )}
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default ConnectAppPage;
